// Serviço para integração com Mercado Pago
// Nota: Para produção, você precisará configurar as credenciais do Mercado Pago

// Declaração de tipo para o MercadoPago no window
declare global {
  interface Window {
    MercadoPago: any;
  }
}

interface MercadoPagoConfig {
  publicKey: string;
  accessToken: string;
  sandbox: boolean;
}

interface PaymentData {
  transaction_amount: number;
  description: string;
  payment_method_id: string;
  payer: {
    email: string;
    identification?: {
      type: string;
      number: string;
    };
  };
  metadata?: {
    user_id: string;
    plan_id: string;
    plan_name: string;
  };
}

class MercadoPagoService {
  private config: MercadoPagoConfig;

  constructor() {
    // Configurações do Mercado Pago
    // Em produção, essas chaves devem vir de variáveis de ambiente
    this.config = {
      publicKey: process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY || 'TEST-af272638-0561-435a-825f-ed94dff2566c',
      accessToken: process.env.REACT_APP_MERCADOPAGO_ACCESS_TOKEN || 'TEST-8521871442707684-091022-c00463723080c2253b14704dac268caa-39045970',
      sandbox: process.env.NODE_ENV !== 'production'
    };
  }

  // Inicializar o SDK do Mercado Pago
  async initializeSDK(): Promise<boolean> {
    try {
      // Verificar se o SDK já foi carregado
      if (window.MercadoPago) {
        console.log('MercadoPago SDK já carregado');
        return true;
      }

      // Carregar o SDK do Mercado Pago
      const script = document.createElement('script');
      script.src = 'https://sdk.mercadopago.com/js/v2';
      script.async = true;
      script.type = 'text/javascript';
      
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Timeout ao carregar MercadoPago SDK'));
        }, 10000); // 10 segundos de timeout

        script.onload = () => {
          clearTimeout(timeout);
          if (window.MercadoPago) {
            console.log('MercadoPago SDK carregado com sucesso');
            resolve(true);
          } else {
            reject(new Error('MercadoPago SDK não carregado'));
          }
        };
        
        script.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('Erro ao carregar MercadoPago SDK'));
        };
        
        document.head.appendChild(script);
      });
    } catch (error) {
      console.error('Erro ao inicializar Mercado Pago:', error);
      return false;
    }
  }

  // Criar preferência de pagamento
  async createPreference(paymentData: PaymentData): Promise<any> {
    try {
      const response = await fetch('https://api.mercadopago.com/checkout/preferences', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.config.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              title: paymentData.description,
              quantity: 1,
              unit_price: paymentData.transaction_amount,
              currency_id: 'BRL'
            }
          ],
          payer: paymentData.payer,
          payment_methods: {
            excluded_payment_methods: [],
            excluded_payment_types: [],
            installments: 12
          },
          back_urls: {
            success: `${window.location.origin}/pagamento/retorno?status=approved`,
            failure: `${window.location.origin}/pagamento/retorno?status=rejected`,
            pending: `${window.location.origin}/pagamento/retorno?status=pending`
          },
          metadata: paymentData.metadata
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Erro detalhado do Mercado Pago:', errorData);
        throw new Error(`Erro na API do Mercado Pago: ${response.status} - ${errorData.message || 'Erro desconhecido'}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erro ao criar preferência:', error);
      throw error;
    }
  }

  // Processar pagamento com cartão
  async processCardPayment(paymentData: PaymentData, cardToken: string): Promise<any> {
    try {
      const response = await fetch('https://api.mercadopago.com/v1/payments', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.config.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transaction_amount: paymentData.transaction_amount,
          token: cardToken,
          description: paymentData.description,
          installments: 1,
          payment_method_id: paymentData.payment_method_id,
          issuer_id: null,
          payer: paymentData.payer,
          metadata: paymentData.metadata
        })
      });

      if (!response.ok) {
        throw new Error(`Erro no pagamento: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erro ao processar pagamento:', error);
      throw error;
    }
  }

  // Verificar status do pagamento
  async getPaymentStatus(paymentId: string): Promise<any> {
    try {
      const response = await fetch(`https://api.mercadopago.com/v1/payments/${paymentId}`, {
        headers: {
          'Authorization': `Bearer ${this.config.accessToken}`,
        }
      });

      if (!response.ok) {
        throw new Error(`Erro ao verificar pagamento: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erro ao verificar status:', error);
      throw error;
    }
  }

  // Criar checkout redirect
  async createCheckoutRedirect(paymentData: PaymentData): Promise<string> {
    try {
      const preference = await this.createPreference(paymentData);
      
      if (this.config.sandbox) {
        return `https://sandbox.mercadopago.com.br/checkout/v1/redirect?pref_id=${preference.id}`;
      } else {
        return `https://www.mercadopago.com.br/checkout/v1/redirect?pref_id=${preference.id}`;
      }
    } catch (error) {
      console.error('Erro ao criar checkout:', error);
      throw error;
    }
  }

  // Criar preferência simples (sem auto_return)
  async createSimplePreference(paymentData: PaymentData): Promise<any> {
    try {
      const response = await fetch('https://api.mercadopago.com/checkout/preferences', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.config.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              title: paymentData.description,
              quantity: 1,
              unit_price: paymentData.transaction_amount,
              currency_id: 'BRL'
            }
          ],
          payer: paymentData.payer,
          payment_methods: {
            excluded_payment_methods: [],
            excluded_payment_types: [],
            installments: 12
          },
          back_urls: {
            success: `${window.location.origin}/pagamento/retorno?status=approved`,
            failure: `${window.location.origin}/pagamento/retorno?status=rejected`,
            pending: `${window.location.origin}/pagamento/retorno?status=pending`
          },
          auto_return: 'approved',
          metadata: paymentData.metadata
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Erro detalhado do Mercado Pago:', errorData);
        throw new Error(`Erro na API do Mercado Pago: ${response.status} - ${errorData.message || 'Erro desconhecido'}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erro ao criar preferência simples:', error);
      throw error;
    }
  }

  // Validar dados do cartão
  validateCardData(cardData: {
    cardNumber: string;
    expirationMonth: string;
    expirationYear: string;
    securityCode: string;
    cardholderName: string;
  }): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validar número do cartão
    if (!cardData.cardNumber || cardData.cardNumber.replace(/\s/g, '').length < 13) {
      errors.push('Número do cartão inválido');
    }

    // Validar mês de expiração
    const month = parseInt(cardData.expirationMonth);
    if (!month || month < 1 || month > 12) {
      errors.push('Mês de expiração inválido');
    }

    // Validar ano de expiração
    const year = parseInt(cardData.expirationYear);
    const currentYear = new Date().getFullYear();
    if (!year || year < currentYear) {
      errors.push('Ano de expiração inválido');
    }

    // Validar código de segurança
    if (!cardData.securityCode || cardData.securityCode.length < 3) {
      errors.push('Código de segurança inválido');
    }

    // Validar nome do portador
    if (!cardData.cardholderName || cardData.cardholderName.trim().length < 2) {
      errors.push('Nome do portador inválido');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// Instância singleton do serviço
export const mercadoPagoService = new MercadoPagoService();

// Tipos para uso em outros componentes
export interface CardData {
  cardNumber: string;
  expirationMonth: string;
  expirationYear: string;
  securityCode: string;
  cardholderName: string;
}

export interface PaymentResult {
  success: boolean;
  paymentId?: string;
  status?: string;
  error?: string;
}

export default mercadoPagoService;
